/**
 * DICTATION — speaking the task instead of typing it.
 *
 * PRIVACY FIRST, BECAUSE IT LEAVES THE BROWSER. The Web Speech API is the
 * browser's own speech service; in most browsers the audio is sent to that
 * vendor's servers to be turned into words. That is acceptable for the
 * SENTENCE that describes a piece of work and for nothing else, and the
 * screen says so in plain words wherever the microphone is offered. Leveri
 * itself never receives audio: only the words the browser hands back, into
 * the same text box a person would have typed them into, where they can be
 * read and corrected before anything is sent.
 *
 * Offered only where the browser provides it. There is no fallback and no
 * polyfill — a microphone that does nothing is worse than no microphone.
 *
 * Nothing starts on its own: listening begins when the button is pressed and
 * ends when it is pressed again, when the person stops speaking, or when the
 * screen goes away.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/* The slice of the Web Speech API this screen uses. It is not in the DOM
   typings TypeScript ships, so it is stated here rather than reached through
   `any`. */
interface SpeechAlternative { transcript: string }
interface SpeechResult { readonly isFinal: boolean; readonly length: number; readonly [index: number]: SpeechAlternative | undefined }
interface SpeechResultList { readonly length: number; readonly [index: number]: SpeechResult | undefined }
interface SpeechResultEvent { resultIndex: number; results: SpeechResultList }
interface SpeechErrorEvent { error: string }
interface SpeechRecognizer {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onerror: ((event: SpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type RecognizerConstructor = new () => SpeechRecognizer;

/** Read once: whether this browser has a speech service at all. */
const RECOGNIZER: RecognizerConstructor | null = (() => {
  if (typeof window === "undefined") return null;
  const host = window as unknown as { SpeechRecognition?: RecognizerConstructor; webkitSpeechRecognition?: RecognizerConstructor };
  return host.SpeechRecognition ?? host.webkitSpeechRecognition ?? null;
})();

/** The sentence shown wherever the microphone is offered. */
export const DICTATION_PRIVACY = "Dictation uses your browser's speech service — speak the task, never client records.";

export interface Dictation {
  /** False where the browser has no speech service; the control is then not drawn. */
  available: boolean;
  listening: boolean;
  /** What is being heard but not yet settled, shown so the person knows it is working. */
  heard: string;
  /** Why dictation stopped, in plain words, or null. */
  problem: string | null;
  toggle: () => void;
  stop: () => void;
}

/**
 * `onWords` receives each settled phrase. It is read through a ref, so the
 * caller may pass a fresh function every render without restarting anything.
 */
export function useDictation(onWords: (words: string) => void): Dictation {
  const [listening, setListening] = useState(false);
  const [heard, setHeard] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const recognizer = useRef<SpeechRecognizer | null>(null);
  const onWordsRef = useRef(onWords);

  useEffect(() => { onWordsRef.current = onWords; }, [onWords]);

  // Leaving the screen ends listening. Abort, not stop: nothing heard after
  // this point should land anywhere.
  useEffect(() => () => {
    const live = recognizer.current;
    recognizer.current = null;
    if (live) { live.onresult = null; live.onend = null; live.onerror = null; live.abort(); }
  }, []);

  const stop = useCallback(() => { recognizer.current?.stop(); }, []);

  const toggle = useCallback(() => {
    if (!RECOGNIZER) return;
    if (recognizer.current) { recognizer.current.stop(); return; }

    const live = new RECOGNIZER();
    live.continuous = true;
    live.interimResults = true;
    live.lang = window.navigator.language || "en";
    live.onresult = (event) => {
      let settled = "";
      let pending = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const words = result?.[0]?.transcript ?? "";
        if (result?.isFinal) settled += words; else pending += words;
      }
      if (settled.trim()) onWordsRef.current(settled.trim());
      setHeard(pending.trim());
    };
    live.onerror = (event) => {
      // Silence and a deliberate stop are not problems; everything else is
      // said plainly, with the way to carry on.
      if (event.error === "no-speech" || event.error === "aborted") return;
      setProblem(
        event.error === "not-allowed" || event.error === "service-not-allowed"
          ? "Dictation needs permission to use the microphone. You can type the task instead."
          : "Dictation stopped. You can type the task instead."
      );
    };
    live.onend = () => {
      if (recognizer.current === live) recognizer.current = null;
      setListening(false);
      setHeard("");
    };

    recognizer.current = live;
    setProblem(null);
    try {
      live.start();
      setListening(true);
    } catch {
      recognizer.current = null;
      setProblem("Dictation could not start. You can type the task instead.");
    }
  }, []);

  return { available: RECOGNIZER !== null, listening, heard, problem, toggle, stop };
}
